import ErrorBoundary from '@/components/ErrorBoundary';
import LanguageRouter from '@/components/LanguageRouter';
import ScrollToTop from '@/components/ScrollToTop';
import { Toaster } from '@/components/ui/toaster';
import { AuthProvider } from '@/contexts/AuthContext';
import { LanguageProvider } from '@/contexts/LanguageContext';
import About from '@/pages/landing/About';
import ActivateAccount from '@/pages/auth/ActivateAccount';
import Admin from '@/pages/admin/Admin';
import CreateProperty from '@/pages/admin/CreateProperty';
import EditProperty from '@/pages/admin/EditProperty';
import Contact from '@/pages/landing/Contact';
import Home from '@/pages/landing/Home';
import Login from '@/pages/auth/Login';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import Properties from '@/pages/Properties';
import PropertyDetail from '@/pages/PropertyDetail';
import PropertyManagement from '@/pages/landing/PropertyManagement';
import PublishProperty from '@/pages/PublishProperty';
import SearchResults from '@/pages/SearchResults';
import BookingDetail from '@/pages/BookingDetail';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { HelmetProvider } from 'react-helmet-async';
import { Route, BrowserRouter as Router, Routes, Navigate } from 'react-router-dom';
import AdminLayout from './components/v2/organisms/admin/layout/AdminLayout';
import ProtectedRoute from './components/v2/organisms/admin/layout/ProtectedRoute';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      staleTime: 5 * 60 * 1000,
    },
  },
});

function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ReactQueryDevtools
          initialIsOpen={false}
          position="right"
          buttonPosition="bottom-right"
        />
        <AuthProvider>
          <Router>
            <ErrorBoundary>
              <ScrollToTop />
              <Routes>
                {/* RUTAS PÚBLICAS - ESPAÑOL */}
                <Route
                  path="/"
                  element={
                    <LanguageProvider>
                      <LanguageRouter />
                    </LanguageProvider>
                  }
                >
                  <Route index element={<Home />} />
                  <Route path="propiedades" element={<Properties />} />
                  <Route
                    path="propiedades/:title"
                    element={<PropertyDetail />}
                  />
                  <Route path="nosotros" element={<About />} />
                  <Route path="contacto" element={<Contact />} />
                  <Route path="gestiondepropiedad" element={<PropertyManagement />} />
                  <Route path="gestion" element={<Navigate to="/gestiondepropiedad" replace />} />
                  <Route path="publicar" element={<PublishProperty />} />
                  <Route path="search" element={<SearchResults />} />
                  <Route
                    path="politicasdeprivacidad"
                    element={<PrivacyPolicy />}
                  />
                </Route>

                {/* RUTAS PÚBLICAS - INGLÉS */}
                <Route
                  path="/en"
                  element={
                    <LanguageProvider>
                      <LanguageRouter />
                    </LanguageProvider>
                  }
                >
                  <Route index element={<Home />} />
                  <Route path="propiedades" element={<Properties />} />
                  <Route
                    path="propiedades/:title"
                    element={<PropertyDetail />}
                  />
                  <Route path="nosotros" element={<About />} />
                  <Route path="contacto" element={<Contact />} />
                  <Route path="gestiondepropiedad" element={<PropertyManagement />} />
                  <Route path="gestion" element={<Navigate to="/en/gestiondepropiedad" replace />} />
                  <Route path="publicar" element={<PublishProperty />} />
                  <Route path="search" element={<SearchResults />} />
                  <Route
                    path="politicasdeprivacidad"
                    element={<PrivacyPolicy />}
                  />
                </Route>

                {/* RUTA PÚBLICA - DETALLE DE RESERVACIÓN PARA HUÉSPEDES */}
                <Route 
                  path="/reservacion/:bookingId" 
                  element={
                    <LanguageProvider>
                      <BookingDetail />
                    </LanguageProvider>
                  } 
                />

                {/* AUTH */}
                <Route path="/login" element={<Login />} />
                <Route path="/activate-account" element={<ActivateAccount />} />

                {/* RUTAS PROTEGIDAS */}
                <Route element={<ProtectedRoute />}>
                  <Route element={<AdminLayout />}>
                    <Route path="/admin" element={<Admin />} />

                    <Route
                      path="/admin/properties/create"
                      element={<CreateProperty />}
                    />

                    <Route
                      path="/admin/properties/edit/:id"
                      element={<EditProperty />}
                    />
                  </Route>
                </Route>
              </Routes>
              <Toaster />
            </ErrorBoundary>
          </Router>
        </AuthProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
